// src/layout/PublicLayout.tsx

import { useEffect, useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { LanguageProvider, useLanguage } from "../contexts/LanguageContext";
import TopBanner from "../components/TopBanner";
import SimpleHeader from "../components/SimpleHeader";
import GovernmentVerificationBanner from "../components/GovernmentVerificationBanner";

import DgaMainHeader from "./dga_v2/DgaMainHeader";
import DgaFooter from "./dga_v2/DgaFooter";
import ScrollToTop from "../components/routing/ScrollToTop";
// Public visitors can use support without sending their main-site login to the bot.
import SupportChatbot from "../components/SupportChatbot/SupportChatbot";
import { CHATBOT_CONFIG } from "../config";

/**
 * Key used to persist embed mode across in-app navigations (same tab).
 * We use sessionStorage (not localStorage) so it resets per tab/session.
 */
const EMBED_STORAGE_KEY = "emasajid_embed";

/**
 * Decide whether we're in "embed mode".
 *
 * Priority order (first match wins):
 *  1) URL query:  embed=1  → embedded, embed=0 → not embedded (also clears sticky flag)
 *  2) Sticky flag: sessionStorage 'emasajid_embed' === "1" → embedded
 *  3) Fallback:    running inside an iframe → embedded
 *
 * NOTE: try/catch guards protect against cross-origin issues when checking window.top.
 */
function computeEmbedMode(search: string): boolean {
  try {
    const params = new URLSearchParams(search);

    // 1) Explicit query wins
    const q = params.get("embed");
    if (q === "1") return true;
    if (q === "0") return false;

    // 2) Sticky flag from session
    if (typeof window !== "undefined") {
      const sticky = window.sessionStorage.getItem(EMBED_STORAGE_KEY);
      if (sticky === "1") return true;
    }

    // 3) Fallback: inside iframe counts as embedded
    if (typeof window !== "undefined" && window.top !== window.self) return true;
  } catch {
    // ignore parsing/cross-origin errors; default to non-embedded
  }
  return false;
}

export default function PublicLayout() {
  const { search, pathname } = useLocation();

  /**
   * Re-evaluate embed mode whenever the querystring changes.
   * (Navigations that keep or drop ?embed will flip this.)
   */
  const isEmbedded = useMemo(() => computeEmbedMode(search), [search]);

  /**
   * Keep a sticky flag in sessionStorage so that once a user enters embed mode
   * (via ?embed=1 or by being inside an iframe), subsequent in-app navigations
   * do NOT need to keep appending the query parameter.
   *
   * Also clear the sticky flag if ?embed=0 is ever present (explicit opt-out).
   */
  useEffect(() => {
    try {
      const params = new URLSearchParams(search);
      const q = params.get("embed");

      if (q === "1" || (q === null && isEmbedded)) {
        // Persist when explicitly set to 1, or when already embedded via iframe
        window.sessionStorage.setItem(EMBED_STORAGE_KEY, "1");
      } else if (q === "0" || (!isEmbedded && window.sessionStorage.getItem(EMBED_STORAGE_KEY) === "1")) {
        // Explicit opt-out OR we’re not embedded anymore → clear sticky flag
        window.sessionStorage.removeItem(EMBED_STORAGE_KEY);
      }

      // OPTIONAL (nice-to-have): once we detect embed=1, clean it from the URL
      // to avoid showing it in the address bar. Safe because we rely on the
      // sticky flag (and iframe) afterwards.
      //
      // if (q === "1") {
      //   const url = new URL(window.location.href);
      //   url.searchParams.delete("embed");
      //   window.history.replaceState(null, "", url.toString());
      // }

    } catch {
      // ignore storage/URL errors silently
    }
  }, [search, isEmbedded]);

  return (
    <LanguageProvider>
      <PublicLayoutContent isEmbedded={isEmbedded} pathname={pathname} />
    </LanguageProvider>
  );
}

function PublicLayoutContent({ isEmbedded, pathname }: { isEmbedded: boolean; pathname: string }) {
  const { t, isRTL } = useLanguage();

  const footerProps = useMemo(
    () => {
      const today = new Date();
      const locale = isRTL ? "ar-SA" : "en-US";
      const lastUpdated = today.toLocaleDateString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

      return {
        background: "DarkGreen" as const,
        NavLinks: true,
        groupLinks: [
          {
            title: t('footerImportantLinks'),
            links: [
              { name: t('footerElectronicServices'), target: "/services" },
              { name: t('footerSearchMosque'), target: "/service/mosque-search" },
              { name: t('footerCharityOpportunities'), target: "/service/available-lands" },
              { name: t('footerMaintenanceOpportunities'), target: "/service/mosque-contribution" },
            ],
          },
        ],
        copyright: t('footerAllRightsReservedDGA'),
        basicLinks: [],
        extraLinks: [
          { name: t('footerDevelopedBy'), target: "#" },
        ],
        bottomImages: [
          "/images/footer-logo.svg",
        ],
        lastUpdated: t('footerLastContentUpdate', { date: lastUpdated }),
      };
    },
    [t, isRTL]
  );

  return (
    <>
      {/* Smooth scroll to top on route changes (kept as-is) */}
      <ScrollToTop smooth />
      {/* One shared widget keeps the conversation open while visitors move between public pages. */}
      {CHATBOT_CONFIG.enabled && CHATBOT_CONFIG.apiBaseUrl &&
        (!isEmbedded || CHATBOT_CONFIG.showWhenEmbedded) && (
          <SupportChatbot language={isRTL ? "ar" : "en"} apiBaseUrl={CHATBOT_CONFIG.apiBaseUrl} />
        )}

      <div className="min-h-screen bg-white">
        {/* Use Project2 structure when not embedded */}
        {!isEmbedded ? (
          <>
            {/* Header Section - Fixed positioning like Project2 */}
            <header className="sticky top-0 z-50 bg-white">
              <GovernmentVerificationBanner />
              <TopBanner />
              <SimpleHeader />
            </header>
            
            {/* Main Content Area - minimal side padding so pages without own container still have margin on mobile */}
            <main
              id="content"
              className="relative px-2 sm:px-4"
              data-embedded={isEmbedded ? "1" : "0"}
              data-pathname={pathname}
            >
              <Outlet />
            </main>
            
            {/* Footer Section */}
            <DgaFooter {...footerProps} />
          </>
        ) : (
          <>
            {/* Fallback to DGA layout when embedded */}
            {!isEmbedded && <DgaMainHeader />}
            <main
              id="content"
              className="max-w-7xl mx-auto px-4 md:px-6"
              data-embedded={isEmbedded ? "1" : "0"}
              data-pathname={pathname}
            >
              <Outlet />
            </main>
            {!isEmbedded && <DgaFooter {...footerProps} />}
          </>
        )}
      </div>
    </>
  );
}

